import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Play, Pause, Share2, Check, Volume2, VolumeX, Music, MicOff, RotateCcw, Users, Lock } from 'lucide-react';
import { SaveSessionButton } from '@/components/sessions/SaveSessionButton';
import { Badge } from '@/components/ui/badge';
import { useEnhancedAudioManager } from '@/hooks/useEnhancedAudioManager';
import { useGlobalBackgroundAudio } from '@/contexts/BackgroundAudioContext';
import { useMeditationAudioUrl } from '@/hooks/useMeditationAudioUrl';
import { useTokenAutoRefresh } from '@/hooks/useTokenAutoRefresh';
import { backendClient } from '@/lib/backendClient';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/utils/apiClient';
// getBackgroundMusicUrl no longer needed - parent passes full URL
import { ShareWithCommunityModal } from '@/components/ShareWithCommunityModal';
import { SocialShareButtons } from '@/components/SocialShareButtons';
import { dbToPercent, percentToDb, DEFAULT_BACKGROUND_VOLUME_DB } from '@/utils/audioVolumeUtils';

interface EnhancedAudioPlayerProps {
  audioUrl: string;
  sessionTitle?: string;
  backgroundAudioUrl?: string; // Single source of truth - full URL to background music
  onEnded?: () => void;
  sessionId?: string;
  lazyLoad?: boolean;
  isCommunitySession?: boolean;
  isSaved?: boolean; // Draft/saved workflow - true = locked settings
  onSaved?: () => void; // Callback when session is saved
  initialBackgroundVolumeDb?: number; // Initial volume for community sessions (bypasses API fetch)
}

// Unified audio ready state
type AudioReadyState = 'idle' | 'loading' | 'ready' | 'error';

const EnhancedAudioPlayer: React.FC<EnhancedAudioPlayerProps> = memo(({
  audioUrl: rawAudioUrl,
  sessionTitle = 'Generated Session',
  backgroundAudioUrl,
  onEnded,
  sessionId: initialSessionId,
  lazyLoad = false,
  isCommunitySession = false,
  isSaved = false,
  onSaved,
  initialBackgroundVolumeDb
}) => {
  // Extract filename from background URL for display
  const backgroundMusicFilename = backgroundAudioUrl ? backgroundAudioUrl.split('/').pop() : undefined;

  // Convert raw audio URL to token-protected streaming URL
  const { streamingUrl: initialAudioUrl, isLoading: isConvertingUrl, error: urlConversionError } = useMeditationAudioUrl(rawAudioUrl);

  // State for tracking playing status (needed for token auto-refresh)
  const [isPlayingState, setIsPlayingState] = useState(false);

  // Auto-refresh streaming token to prevent expiry during playback
  const { currentStreamingUrl: audioUrl, isRefreshing: isRefreshingToken } = useTokenAutoRefresh({
    streamingUrl: initialAudioUrl,
    isPlaying: isPlayingState,
    enabled: !rawAudioUrl?.startsWith('blob:') && !rawAudioUrl?.includes('/audio/shared/') && !rawAudioUrl?.includes('/audio/community/')
  });

  const [volume, setVolume] = useState(100);  // Changed default from 85% to 100%
  // Use initialBackgroundVolumeDb if provided (e.g., for community sessions), otherwise use default
  const effectiveInitialVolumeDb = initialBackgroundVolumeDb ?? DEFAULT_BACKGROUND_VOLUME_DB;
  const [backgroundVolume, setBackgroundVolume] = useState(dbToPercent(effectiveInitialVolumeDb));
  const [backgroundVolumeDb, setBackgroundVolumeDb] = useState<number>(effectiveInitialVolumeDb);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoadingVolume, setIsLoadingVolume] = useState(false);
  const [isSavingVolume, setIsSavingVolume] = useState(false);
  const saveVolumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [hasSessionEnded, setHasSessionEnded] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId || null);
  const [isPollingForSession, setIsPollingForSession] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadingTimeoutId, setLoadingTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  // Global background audio context for auto-stop timer
  const { startAutoStopTimer, cancelAutoStopTimer } = useGlobalBackgroundAudio();

  // Update sessionId when prop changes
  useEffect(() => {
    if (initialSessionId) {
      setSessionId(initialSessionId);
      setIsPollingForSession(false);
    }
  }, [initialSessionId]);

  // Load saved background music volume when sessionId becomes available
  useEffect(() => {
    const loadSavedVolume = async () => {
      if (!sessionId || isCommunitySession) return;

      setIsLoadingVolume(true);
      try {
        const response = await apiClient.get(`/sessions/${sessionId}`, {
          includeAuth: true
        });

        if (response.background_music_volume_db !== null && response.background_music_volume_db !== undefined) {
          const volumeDb = response.background_music_volume_db;
          const volumePercent = dbToPercent(volumeDb);

          setBackgroundVolumeDb(volumeDb);
          setBackgroundVolume(volumePercent);

          console.log('[EnhancedAudioPlayer] Loaded saved volume:', { volumeDb, volumePercent });
        }
      } catch (error) {
        console.error('[EnhancedAudioPlayer] Failed to load saved volume:', error);
      } finally {
        setIsLoadingVolume(false);
      }
    };

    loadSavedVolume();
  }, [sessionId, isCommunitySession]);

  // Debounced auto-save for background volume changes
  useEffect(() => {
    if (!sessionId || isCommunitySession) return;

    // Clear existing timeout
    if (saveVolumeTimeoutRef.current) {
      clearTimeout(saveVolumeTimeoutRef.current);
    }

    // Debounce volume save (500ms after last change)
    saveVolumeTimeoutRef.current = setTimeout(async () => {
      const volumeDb = percentToDb(backgroundVolume);

      // Only save if volume has actually changed
      if (volumeDb === backgroundVolumeDb) return;

      setIsSavingVolume(true);
      try {
        await apiClient.patch(`/sessions/${sessionId}/volume`, {
          background_music_volume_db: volumeDb
        }, {
          includeAuth: true
        });

        setBackgroundVolumeDb(volumeDb);
        console.log('[EnhancedAudioPlayer] Auto-saved volume:', { volumeDb, volumePercent: backgroundVolume });
      } catch (error) {
        console.error('[EnhancedAudioPlayer] Failed to auto-save volume:', error);
      } finally {
        setIsSavingVolume(false);
      }
    }, 500);

    return () => {
      if (saveVolumeTimeoutRef.current) {
        clearTimeout(saveVolumeTimeoutRef.current);
      }
    };
  }, [backgroundVolume, backgroundVolumeDb, sessionId, isCommunitySession]);

  // Save volume on page unload/navigation
  useEffect(() => {
    if (!sessionId || isCommunitySession) return;

    const handleBeforeUnload = async () => {
      const volumeDb = percentToDb(backgroundVolume);

      // Only save if volume has changed
      if (volumeDb === backgroundVolumeDb) return;

      try {
        // Use sendBeacon for reliable unload requests
        const data = JSON.stringify({ background_music_volume_db: volumeDb });
        const blob = new Blob([data], { type: 'application/json' });

        // Get auth token for the request
        const token = localStorage.getItem('backendClient.auth.token');
        const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;
        const url = `${baseUrl}/api/v1/sessions/${sessionId}/volume`;

        // Note: sendBeacon doesn't support custom headers, so we'll use fetch with keepalive
        navigator.sendBeacon(url, blob) || fetch(url, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: data,
          keepalive: true
        });

        console.log('[EnhancedAudioPlayer] Saved volume on unload:', volumeDb);
      } catch (error) {
        console.error('[EnhancedAudioPlayer] Failed to save volume on unload:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [backgroundVolume, backgroundVolumeDb, sessionId, isCommunitySession]);

  // Poll for sessionId if not provided but we have audioUrl
  useEffect(() => {
    if (!sessionId && rawAudioUrl && !isPollingForSession) {
      setIsPollingForSession(true);

      const pollForSession = async () => {
        try {
          const baseUrl = rawAudioUrl.split('?')[0];
          const { data: sessions, error } = await backendClient
            .from('sessions')
            .select('id, audio_url, created_at')
            .order('created_at', { ascending: false })
            .limit(10);

          if (error) return false;

          const session = sessions?.find(s => {
            const sessionBaseUrl = s.audio_url?.split('?')[0];
            return sessionBaseUrl === baseUrl;
          });

          if (session) {
            setSessionId(session.id);
            setIsPollingForSession(false);
            return true;
          }

          return false;
        } catch (error) {
          return false;
        }
      };

      let attempts = 0;
      const maxAttempts = 60;
      const pollInterval = setInterval(async () => {
        attempts++;
        const found = await pollForSession();

        if (found || attempts >= maxAttempts) {
          clearInterval(pollInterval);
          if (!found) {
            setIsPollingForSession(false);
          }
        }
      }, 1000);

      return () => clearInterval(pollInterval);
    }
  }, [sessionId, rawAudioUrl, isPollingForSession]);

  // Background audio URL is now passed directly (single source of truth)
  // Parent components handle backward compatibility if needed

  // Memoize enhanced audio manager props
  const audioManagerProps = useMemo(() => ({
    mainAudioUrl: audioUrl,
    backgroundAudioUrl,
    backgroundVolume: backgroundVolume / 100,
    autoPlayBackground: !!backgroundAudioUrl,
    lazyLoad
  }), [audioUrl, backgroundAudioUrl, backgroundVolume, lazyLoad]);

  // Enhanced audio manager
  const {
    playWithBackground,
    pause,
    seekTo,
    isPlaying,
    isMainAudioReady,
    isBackgroundAudioReady,
    isBackgroundAudioLoading,
    isBackgroundPlaying,
    isBackgroundMuted,
    updateMainVolume,
    updateBackgroundVolume,
    updatePlaybackRate,
    currentTime,
    duration,
    initializeAudioContext,
    stopBackgroundAudio: stopMixedBackground,
    pauseBackgroundAudio: pauseMixedBackground,
    resumeBackgroundAudio: resumeMixedBackground,
    playBackgroundAudio: playMixedBackground
  } = useEnhancedAudioManager(audioManagerProps);

  // Sync isPlaying state for token auto-refresh
  useEffect(() => {
    setIsPlayingState(isPlaying);
  }, [isPlaying]);

  // Update volumes
  useEffect(() => {
    const actualVolume = isMuted ? 0 : volume / 100;
    updateMainVolume(actualVolume);
  }, [volume, isMuted, updateMainVolume]);

  // Handle session end
  useEffect(() => {
    if (!hasSessionEnded && !isPlaying && currentTime > 0 && duration > 0 && currentTime >= duration) {
      setHasSessionEnded(true);
      startAutoStopTimer(20);
      onEnded?.();
    }
  }, [hasSessionEnded, isPlaying, currentTime, duration, startAutoStopTimer, onEnded]);

  // Reset session end state
  useEffect(() => {
    if (isPlaying || currentTime === 0) {
      setHasSessionEnded(false);
    }
  }, [isPlaying, currentTime]);

  // Enhanced timeout detection with auto-retry for stuck loading states
  const maxRetries = 3;
  const retryTimeoutMs = 5000;

  // Create refs to access audio manager in timeout callback
  const audioManagerRef = React.useRef({
    playWithBackground,
    initializeAudioContext
  });

  // Update ref on every render to keep it current
  useEffect(() => {
    audioManagerRef.current = {
      playWithBackground,
      initializeAudioContext
    };
  });

  useEffect(() => {
    // Clear existing timeout if any
    if (loadingTimeoutId) {
      clearTimeout(loadingTimeoutId);
      setLoadingTimeoutId(null);
    }

    if (audioUrl && !isConvertingUrl && !isMainAudioReady && !lazyLoad) {
      console.log('[EnhancedAudioPlayer] Starting loading timer', {
        retryCount,
        maxRetries,
        audioUrl: audioUrl?.substring(0, 50)
      });

      const timeout = setTimeout(async () => {
        console.warn(`[EnhancedAudioPlayer] Loading timeout - attempt ${retryCount + 1} of ${maxRetries}`);

        if (retryCount < maxRetries) {
          console.log('[EnhancedAudioPlayer] Attempting auto-retry initialization');
          setRetryCount(prev => prev + 1);

          // Attempt to re-initialize through play action
          try {
            await audioManagerRef.current.initializeAudioContext();
            // Trigger a play attempt which will use the fallback initialization
            await audioManagerRef.current.playWithBackground(1.0);
            console.log('[EnhancedAudioPlayer] Auto-retry successful');
          } catch (err) {
            console.error('[EnhancedAudioPlayer] Auto-retry failed:', err);
          }
        } else {
          console.error('[EnhancedAudioPlayer] Max retries reached - audio initialization failed');
          toast({
            title: "Audio Loading Failed",
            description: "Unable to load audio. Please refresh the page and try again.",
            variant: "destructive"
          });
        }
      }, retryTimeoutMs);

      setLoadingTimeoutId(timeout);
    } else if (isMainAudioReady && retryCount > 0) {
      // Reset retry count on successful load
      console.log('[EnhancedAudioPlayer] Audio ready - resetting retry count');
      setRetryCount(0);
    }

    // Cleanup timeout on unmount or dependency change
    return () => {
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
      }
    };
  }, [audioUrl, isConvertingUrl, isMainAudioReady, lazyLoad, retryCount, toast]);

  // Compute unified audio ready state
  const audioReadyState: AudioReadyState = useMemo(() => {
    if (urlConversionError) return 'error';
    if (isConvertingUrl) return 'loading';
    if (!audioUrl) return 'idle';

    // For lazy load, consider ready if URL is available (will initialize on play)
    if (lazyLoad && audioUrl) return 'ready';

    if (!isMainAudioReady) return 'loading';
    return 'ready';
  }, [urlConversionError, isConvertingUrl, audioUrl, isMainAudioReady, lazyLoad]);

  // Compute status message
  const statusMessage = useMemo(() => {
    switch (audioReadyState) {
      case 'error':
        return `Error: ${urlConversionError?.message || 'Failed to load audio'}`;
      case 'loading':
        if (isConvertingUrl) return 'Generating secure streaming token...';
        if (lazyLoad && audioUrl) return 'Ready - Click play to load';
        if (!isMainAudioReady) return 'Loading audio player...';
        return 'Loading...';
      case 'ready':
        if (lazyLoad && !isMainAudioReady) return 'Ready - Click play to load';
        if (backgroundAudioUrl) {
          if (isBackgroundAudioLoading) {
            return 'Ready to play (loading background music...)';
          }
          return isBackgroundAudioReady
            ? 'Ready to play (with background music)'
            : 'Ready to play (voice only - background music unavailable)';
        }
        return 'Ready to play';
      case 'idle':
      default:
        return 'No audio loaded';
    }
  }, [audioReadyState, urlConversionError, isConvertingUrl, isMainAudioReady, backgroundAudioUrl, isBackgroundAudioReady, isBackgroundAudioLoading, lazyLoad, audioUrl]);

  const canPlay = audioReadyState === 'ready';

  const handlePlayPause = useCallback(async () => {
    if (isPlaying) {
      pause();
      pauseMixedBackground(); // Mute background music when pausing
    } else {
      cancelAutoStopTimer();

      try {
        await initializeAudioContext();
        await playWithBackground(playbackRate);
        resumeMixedBackground(); // Resume background music when playing
      } catch (error) {
        console.error('❌ Playback failed:', error);
        toast({
          title: "Playback Error",
          description: error instanceof Error ? error.message : "Failed to start audio. Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [isPlaying, pause, pauseMixedBackground, playWithBackground, resumeMixedBackground, playbackRate, initializeAudioContext, cancelAutoStopTimer, toast]);

  const handleSpeedChange = useCallback((rate: number) => {
    setPlaybackRate(rate);
    updatePlaybackRate(rate);
  }, [updatePlaybackRate]);

  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted);
  }, [isMuted]);

  const handleSeek = useCallback((value: number[]) => {
    seekTo(value[0]);
  }, [seekTo]);

  // Share functionality
  const handleShare = useCallback(async () => {
    if (!sessionId) {
      toast({
        title: "Share Unavailable",
        description: "This session cannot be shared at this time.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingShare(true);
    try {
      const existingResponse = await apiClient.get(`/sessions/${sessionId}/share`, {
        includeAuth: true
      });

      const linkToShare = existingResponse.share_url || (await apiClient.post(`/sessions/${sessionId}/share`, {}, {
        includeAuth: true
      })).share_url;

      setShareLink(linkToShare);
      await navigator.clipboard.writeText(linkToShare);
      setCopiedShare(true);
      toast({
        title: "Link Copied!",
        description: "Share this permanent link with anyone.",
      });
      setTimeout(() => setCopiedShare(false), 2000);
    } catch (error: any) {
      toast({
        title: "Share Failed",
        description: error.message || "Failed to generate share link",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingShare(false);
    }
  }, [sessionId, toast]);

  const nativeShare = useCallback(async () => {
    if (!shareLink || !navigator.share) return;

    try {
      await navigator.share({
        title: sessionTitle,
        text: `Listen to this meditation: ${sessionTitle}`,
        url: shareLink
      });
    } catch (err) {
      // User cancelled
    }
  }, [shareLink, sessionTitle]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Card className="w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Music className="h-5 w-5" />
          {sessionTitle}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Progress Bar */}
        <div className="space-y-2">
          <Slider
            value={[currentTime]}
            max={duration || 100}
            step={0.1}
            className="w-full cursor-pointer"
            disabled={!canPlay}
            onValueChange={handleSeek}
          />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration - currentTime)}</span>
          </div>
        </div>

        {/* Main Controls */}
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={handlePlayPause}
            disabled={!canPlay}
            className="h-12 w-12 rounded-full"
            title={canPlay ? (isPlaying ? 'Pause' : 'Play') : statusMessage}
          >
            {audioReadyState === 'loading' ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
            ) : isPlaying ? (
              <Pause className="h-5 w-5" />
            ) : (
              <Play className="h-5 w-5" />
            )}
          </Button>
        </div>

        {/* Status Message */}
        <div className="text-center">
          <p className={`text-sm ${
            audioReadyState === 'ready' ? 'text-green-600' :
            audioReadyState === 'error' ? 'text-destructive' :
            audioReadyState === 'loading' ? 'text-blue-600' :
            'text-muted-foreground'
          }`}>
            {audioReadyState === 'ready' && '✅ '}
            {audioReadyState === 'error' && '❌ '}
            {audioReadyState === 'loading' && '⏳ '}
            {statusMessage}
          </p>
        </div>

        {/* Volume Controls */}
        <div className="space-y-4">
          {/* Main Volume */}
          <div className="space-y-2">
            <div className="text-sm font-medium">Voice Volume</div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleMute}
                className="px-2"
              >
                {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </Button>
              <div className="flex-1">
                <Slider
                  value={[isMuted ? 0 : volume]}
                  max={100}
                  step={1}
                  onValueChange={(value) => setVolume(value[0])}
                  className="w-full"
                />
              </div>
              <span className="text-sm text-muted-foreground w-10 text-right">
                {isMuted ? 0 : volume}%
              </span>
            </div>
          </div>

          {/* Background Music Volume - Only show for personal sessions with background music */}
          {backgroundAudioUrl && !isCommunitySession && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Music className="h-4 w-4" />
                  <span className="text-sm font-medium">Background Music Volume</span>
                  {isSaved && (
                    <Badge variant="secondary" className="text-xs">
                      <Lock className="h-3 w-3 mr-1" />
                      Locked
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isSavingVolume && (
                    <span className="text-xs text-muted-foreground">Saving...</span>
                  )}
                  {sessionId && !isSaved && (
                    <SaveSessionButton
                      sessionId={sessionId}
                      isSaved={isSaved}
                      hasBackgroundMusic={!!backgroundAudioUrl}
                      onSaved={onSaved}
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (isBackgroundMuted) {
                      resumeMixedBackground();
                    } else {
                      pauseMixedBackground();
                    }
                  }}
                  className="px-2"
                  disabled={!isBackgroundAudioReady}
                >
                  {isBackgroundMuted ? <VolumeX className="h-4 w-4" /> : <Music className="h-4 w-4" />}
                </Button>
                <div className="flex-1">
                  <Slider
                    value={[backgroundVolume]}
                    max={100}
                    step={1}
                    onValueChange={(value) => {
                      if (isSaved) return; // Prevent changes when saved
                      const newVolume = value[0];
                      setBackgroundVolume(newVolume);
                      updateBackgroundVolume(newVolume / 100);
                    }}
                    className="w-full"
                    disabled={!isBackgroundAudioReady || isSaved}
                    title={isSaved ? "Volume is locked for saved sessions" : "Adjust background music volume"}
                  />
                </div>
                <span className="text-sm text-muted-foreground w-10 text-right">
                  {Math.round(backgroundVolume)}%
                </span>
              </div>
              {sessionId && (
                <p className="text-xs text-muted-foreground">
                  {isSaved ? (
                    <span>Volume is locked. Shared links will use this volume setting.</span>
                  ) : (
                    <>
                      Adjust the volume, then click "Save Session" to lock your settings.
                      <span className="block mt-1 text-amber-600 dark:text-amber-400">
                        Draft sessions share voice-only audio until saved.
                      </span>
                    </>
                  )}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Playback Speed */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium">Playback Speed</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                {Math.round(playbackRate * 100)}%
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSpeedChange(0.95)}
                className="p-1 h-8 w-8"
                title="Reset to 0.95x"
              >
                <RotateCcw className="h-3 w-3" />
              </Button>
            </div>
          </div>

          <Slider
            value={[playbackRate]}
            min={0.6}
            max={1.3}
            step={0.01}
            onValueChange={(value) => handleSpeedChange(value[0])}
            className="w-full"
          />

          {/* Speed Preset Buttons */}
          <div className="flex flex-wrap gap-1 justify-center">
            {[0.6, 0.7, 0.8, 0.85, 0.9, 0.95, 1.0, 1.1].map((rate) => (
              <Button
                key={rate}
                variant={playbackRate === rate ? "default" : "outline"}
                size="sm"
                onClick={() => handleSpeedChange(rate)}
                className="text-xs px-2 py-1 h-7"
              >
                {rate}x
              </Button>
            ))}
          </div>
        </div>

        {/* Share Options */}
        <div className="space-y-4">
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={shareLink && navigator.share ? nativeShare : handleShare}
              disabled={!sessionId || isGeneratingShare}
              className="min-w-[180px]"
            >
              {copiedShare ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Link Copied!
                </>
              ) : (
                <>
                  <Share2 className="h-4 w-4 mr-2" />
                  {isGeneratingShare ? 'Generating...' : 'Get Share Link'}
                </>
              )}
            </Button>

            {!isCommunitySession && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCommunityModal(true)}
                disabled={!sessionId || !isSaved}
                title={!isSaved ? "Save your session first to share with the community" : undefined}
                className="min-w-[180px]"
              >
                <Users className="h-4 w-4 mr-2" />
                Share with Community
              </Button>
            )}
          </div>

          {!sessionId && (
            <p className="text-xs text-center text-muted-foreground">
              {isPollingForSession
                ? '🔄 Looking for session... (retrying)'
                : 'Session is being saved... Share will be available shortly'}
            </p>
          )}

          {sessionId && !isSaved && !isCommunitySession && (
            <p className="text-xs text-center text-amber-600 dark:text-amber-400">
              💡 Save your session to enable community sharing and lock your volume settings.
            </p>
          )}

          {sessionId && !shareLink && backgroundAudioUrl && isSaved && (
            <p className="text-xs text-center text-muted-foreground">
              💡 Tip: Your volume settings are locked. Generate a share link to share this session.
            </p>
          )}

          {sessionId && !shareLink && backgroundAudioUrl && !isSaved && (
            <p className="text-xs text-center text-muted-foreground">
              💡 Tip: Adjust background music volume before saving — the current volume will be locked when you save.
            </p>
          )}

          {shareLink && (
            <>
              <div className="text-xs text-center">
                <p className="text-muted-foreground mb-2">Share Link:</p>
                <div className="bg-muted/50 rounded px-3 py-2 font-mono text-xs break-all">
                  {shareLink}
                </div>
              </div>

              {/* Social Media Share Buttons */}
              <div className="pt-2">
                <p className="text-xs text-center text-muted-foreground mb-3">
                  Share on social media:
                </p>
                <SocialShareButtons
                  url={shareLink}
                  title={sessionTitle}
                  text={`Listen to this meditation: ${sessionTitle}`}
                  compact={true}
                  showLabels={true}
                  className="justify-center"
                />
              </div>
            </>
          )}
        </div>

        {backgroundMusicFilename && (
          <div className="text-xs text-muted-foreground text-center">
            {isBackgroundAudioLoading ? (
              <>⏳ Loading background music...</>
            ) : isBackgroundAudioReady ? (
              <>🎵 Background music: {backgroundMusicFilename}</>
            ) : (
              <>⚠️ Background music unavailable ({backgroundMusicFilename})</>
            )}
          </div>
        )}
      </CardContent>

      {/* Community Share Modal */}
      <ShareWithCommunityModal
        isOpen={showCommunityModal}
        onClose={() => setShowCommunityModal(false)}
        sessionId={sessionId || undefined}
        sessionTitle={sessionTitle}
      />
    </Card>
  );
});

export default EnhancedAudioPlayer;
